import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Table,
    Button,
    Spinner,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    Alert
} from 'reactstrap';
import { toast } from 'react-toastify';
import { getUserFromLocalStorage } from '../../utilities/user';
import { colors } from './partnerTheme';

const API_URL = process.env.REACT_APP_SERVER_URL || '';

const StaffList = () => {
    const history = useHistory();
    // Use agent from auth state, fallback to localStorage (matching PartnerDashboard pattern)
    const { agent } = useSelector((state) => state.auth);
    const user = agent || getUserFromLocalStorage();

    const token = user?.token;
    const partnerId = user?.result?.partnerId || user?.result?._id;
    const operationMode = user?.result?.operationMode;
    const isPartnerAccount = operationMode === '3PL_PARTNER';
    const isStaffAccount = operationMode === '3PL_STAFF';
    const userPermissions = user?.result?.permissions || {};

    // Check if user has permission to manage staff (partners always can, staff need permission)
    const canManageStaff = isPartnerAccount || userPermissions?.staff === 'full';
    const canViewStaff = isPartnerAccount || ['view', 'full'].includes(userPermissions?.staff);

    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [passwordModal, setPasswordModal] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        // Permission filtering is now handled at route level in Admin.js
        // Users without permission won't see this page in the menu
        fetchStaff();
    }, []);

    const fetchStaff = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/api/3pl-staff`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setStaff(response.data.data.staff || []);
            }
        } catch (err) {
            console.error('Error fetching staff:', err);
            setError(err.response?.data?.error?.message || 'Failed to fetch staff members');
            toast.error('Failed to load staff members');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (staffMember) => {
        if (!canManageStaff) {
            toast.error("You don't have permission to modify staff");
            return;
        }

        try {
            const newStatus = staffMember.status === 'active' ? 'suspended' : 'active';
            await axios.put(`${API_URL}/api/3pl-staff/${staffMember._id}`,
                { status: newStatus, isActive: newStatus === 'active' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success(`Staff ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
            fetchStaff();
        } catch (err) {
            console.error('Error updating staff status:', err);
            toast.error(err.response?.data?.error?.message || 'Failed to update status');
        }
    };

    const handleDelete = async () => {
        if (!selectedStaff) return;

        try {
            await axios.delete(`${API_URL}/api/3pl-staff/${selectedStaff._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Staff member deleted successfully');
            setDeleteModal(false);
            setSelectedStaff(null);
            fetchStaff();
        } catch (err) {
            console.error('Error deleting staff:', err);
            toast.error(err.response?.data?.error?.message || 'Failed to delete staff');
        }
    };

    const handleResetPassword = async () => {
        if (!selectedStaff || !newPassword) return;

        if (newPassword.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        try {
            await axios.put(`${API_URL}/api/3pl-staff/${selectedStaff._id}/password`,
                { newPassword },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Password reset successfully');
            setPasswordModal(false);
            setSelectedStaff(null);
            setNewPassword('');
        } catch (err) {
            console.error('Error resetting password:', err);
            toast.error(err.response?.data?.error?.message || 'Failed to reset password');
        }
    };

    // Preferred flow: email the staff member a set-password link (1h) — no
    // plaintext password ever passes through the partner.
    const handleSendResetLink = async () => {
        if (!selectedStaff?.email) return toast.error('This staff member has no email on file.');
        try {
            // No `url`: the reset-form origin is server configuration only.
            await axios.post(`${API_URL}/api/3pl-staff/forgot-password`, {
                email: selectedStaff.email,
            });
            toast.success(`Reset link emailed to ${selectedStaff.email}`);
            setPasswordModal(false);
            setSelectedStaff(null);
            setNewPassword('');
        } catch (err) {
            console.error('Error sending reset link:', err);
            toast.error(err.response?.data?.error?.message || 'Failed to send reset link');
        }
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            active: { bg: colors.green, text: 'Active' },
            suspended: { bg: colors.amber, text: 'Suspended' },
            inactive: { bg: colors.red, text: 'Inactive' }
        };
        const config = statusColors[status] || statusColors.inactive;
        return (
            <Badge style={{ backgroundColor: config.bg, color: colors.white, padding: '6px 12px' }}>
                {config.text}
            </Badge>
        );
    };

    const getPermissionBadges = (permissions) => {
        if (!permissions) return null;

        const permissionLabels = {
            riders: { label: 'Riders', icon: 'nc-icon nc-delivery-fast' },
            financials: { label: 'Financials', icon: 'nc-icon nc-money-coins' },
            orders: { label: 'Orders', icon: 'nc-icon nc-cart-simple' },
            tracking: { label: 'Tracking', icon: 'nc-icon nc-pin-3' },
            profile: { label: 'Profile', icon: 'nc-icon nc-circle-09' },
            staff: { label: 'Staff', icon: 'nc-icon nc-badge' },
            analytics: { label: 'Analytics', icon: 'nc-icon nc-chart-bar-32' }
        };

        return Object.entries(permissions)
            .filter(([key, value]) => value !== 'none')
            .map(([key, value]) => (
                <Badge
                    key={key}
                    style={{
                        backgroundColor: value === 'full' ? colors.green : colors.blue,
                        marginRight: '4px',
                        marginBottom: '4px',
                        fontSize: '10px',
                        padding: '4px 8px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}
                >
                    <i className={permissionLabels[key]?.icon || ''} style={{ fontSize: '10px' }} />
                    {permissionLabels[key]?.label || key}
                </Badge>
            ));
    };

    const filteredStaff = staff.filter(s =>
        s.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.role?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Spinner color="primary" />
            </div>
        );
    }

    return (
        <div className="content">
            <Row>
                <Col md="12">
                    <Card>
                        <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <CardTitle tag="h4" style={{ color: colors.blue, marginBottom: 0 }}>
                                    <i className="nc-icon nc-badge" style={{ marginRight: '8px' }} />
                                    Staff Management
                                </CardTitle>
                                <p style={{ color: colors.muted, marginBottom: 0, marginTop: '4px' }}>
                                    Manage your team members and their access permissions
                                </p>
                            </div>
                            {canManageStaff && (
                                <Button
                                    style={{ backgroundColor: colors.green, borderColor: colors.green }}
                                    onClick={() => history.push('/admin/3pl-staff-create')}
                                >
                                    + Add Staff
                                </Button>
                            )}
                        </CardHeader>
                        <CardBody>
                            {error && <Alert color="danger">{error}</Alert>}

                            {/* Search Bar */}
                            <div style={{ marginBottom: '20px' }}>
                                <Input
                                    type="text"
                                    placeholder="Search by name, email, or role..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{ maxWidth: '400px' }}
                                />
                            </div>

                            {filteredStaff.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: colors.muted }}>
                                    {staff.length === 0 ? (
                                        <>
                                            <p style={{ fontSize: '18px', marginBottom: '10px' }}>No staff members yet</p>
                                            <p>Add your first team member to get started</p>
                                            {canManageStaff && (
                                                <Button
                                                    style={{ backgroundColor: colors.blue, borderColor: colors.blue, marginTop: '10px' }}
                                                    onClick={() => history.push('/admin/3pl-staff-create')}
                                                >
                                                    Add Staff Member
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        <p>No staff members match your search</p>
                                    )}
                                </div>
                            ) : (
                                <Table responsive hover>
                                    <thead style={{ backgroundColor: colors.blue, color: colors.white }}>
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th>Permissions</th>
                                            <th>Status</th>
                                            <th>Last Login</th>
                                            {canManageStaff && <th>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredStaff.map((member) => (
                                            <tr key={member._id}>
                                                <td style={{ fontWeight: 500 }}>
                                                    {member.firstName} {member.lastName}
                                                </td>
                                                <td>{member.email}</td>
                                                <td>{member.role || 'Staff'}</td>
                                                <td style={{ maxWidth: '200px' }}>
                                                    {getPermissionBadges(member.permissions)}
                                                </td>
                                                <td>{getStatusBadge(member.status)}</td>
                                                <td>
                                                    {member.lastLoginAt
                                                        ? new Date(member.lastLoginAt).toLocaleDateString()
                                                        : 'Never'
                                                    }
                                                </td>
                                                {canManageStaff && (
                                                    <td>
                                                        <Button
                                                            size="sm"
                                                            style={{
                                                                backgroundColor: colors.blue,
                                                                borderColor: colors.blue,
                                                                marginRight: '5px'
                                                            }}
                                                            onClick={() => history.push(`/admin/3pl-staff-edit/${member._id}`)}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            style={{
                                                                backgroundColor: member.status === 'active' ? colors.amber : colors.green,
                                                                borderColor: member.status === 'active' ? colors.amber : colors.green,
                                                                marginRight: '5px'
                                                            }}
                                                            onClick={() => handleStatusToggle(member)}
                                                        >
                                                            {member.status === 'active' ? 'Suspend' : 'Activate'}
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            color="secondary"
                                                            style={{ marginRight: '5px' }}
                                                            onClick={() => {
                                                                setSelectedStaff(member);
                                                                setPasswordModal(true);
                                                            }}
                                                        >
                                                            Reset Password
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            style={{ backgroundColor: colors.red, borderColor: colors.red }}
                                                            onClick={() => {
                                                                setSelectedStaff(member);
                                                                setDeleteModal(true);
                                                            }}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <ModalHeader toggle={() => setDeleteModal(false)}>
                    Confirm Delete
                </ModalHeader>
                <ModalBody>
                    Are you sure you want to delete <strong>{selectedStaff?.firstName} {selectedStaff?.lastName}</strong>?
                    This action cannot be undone.
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button style={{ backgroundColor: colors.red, borderColor: colors.red }} onClick={handleDelete}>
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Password Reset Modal */}
            <Modal isOpen={passwordModal} toggle={() => { setPasswordModal(false); setNewPassword(''); }}>
                <ModalHeader toggle={() => { setPasswordModal(false); setNewPassword(''); }}>
                    Reset Password for {selectedStaff?.firstName}
                </ModalHeader>
                <ModalBody>
                    <div style={{ marginBottom: 14 }}>
                        <Button
                            style={{ backgroundColor: colors.green || '#16a34a', borderColor: colors.green || '#16a34a', width: '100%' }}
                            onClick={handleSendResetLink}
                        >
                            ✉ Email Reset Link (recommended)
                        </Button>
                        <small style={{ display: 'block', textAlign: 'center', color: '#6b7280', marginTop: 6 }}>
                            Sends {selectedStaff?.email || 'the staff member'} a secure link (valid 1 hour) to set their own password
                        </small>
                    </div>
                    <hr />
                    <small style={{ color: '#6b7280' }}>Or set a password for them directly:</small>
                    <Input
                        type="password"
                        placeholder="Enter new password (min 8 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        style={{ marginTop: 6 }}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => { setPasswordModal(false); setNewPassword(''); }}>
                        Cancel
                    </Button>
                    <Button
                        style={{ backgroundColor: colors.blue, borderColor: colors.blue }}
                        onClick={handleResetPassword}
                        disabled={newPassword.length < 8}
                    >
                        Reset Password
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default StaffList;
